import { CommonInput, FlexBox, INPUT_TYPES } from "@brg/ui";
import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import DeleteIcon from "shared/icons/DeleteIcon";
import { DefaultButton } from "../styles";
import CheckboxField from "./CheckboxField";
import { IExternalShowcase } from "../types";
import { LayoutConst, useModal } from "core";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { Lang } from "@brg/api";
import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { getTeamEditLangSwitcherLabels } from "../helpers/teamEditLangSwitcher";

const PublicShowcaseCheckboxWrap = styled.div`
    & > * > *:first-child {
        min-width: unset !important;
    }
`;

const Wrapper = styled.div`
    width: 100%;
    max-width: 860px;
    background: #ffffff;
    border-radius: 6px;
    padding: 24px;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        max-width: 760px;
        padding: 20px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
        max-width: 100%;
        padding: 20px 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 16px 12px;
    }
`;

export const ScrollContainer = styled(FlexBox)`
    overflow-y: auto;
    flex-flow: column;
    max-height: 260px;
    width: 100%;

  ::-webkit-scrollbar {
    width: 6px; /* ширина scrollbar */
    height: 61px;
  }
  ::-webkit-scrollbar-track {
    background: transparent; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 3px; /* закругления плашки */
    background: #909090;
    border: 1.55px solid rgba(0, 0, 0, 0); /* имитация марджина */
    background-clip: padding-box; /* имитация марджина */
  }

`

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
    width:100%;
`;

const Column = styled.div`
    display: flex;
    flex-flow: column;
    gap: 12px;
    width: 100%;
`;

const IconButton = styled.button`
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
`;

const Footer = styled(FlexBox)`

`;

export interface ExternalShowcasesSavePayload {
    items: IExternalShowcase[];
    isPublicShowcase: boolean;
}

interface ExternalShowcasesEditModalProps {
    items?: IExternalShowcase[];
    isPublicShowcase?: boolean;
    onSave: (payload: ExternalShowcasesSavePayload) => void;
}

const getEmptyShowcase = (): IExternalShowcase => ({
    title: createDefaultLangObject(""),
    context: "",
    subtitle: createDefaultLangObject(""),
});

const toSafeTitle = (title?: LangObject[]): LangObject[] => {
    const nextTitle = Array.isArray(title) ? [...title] : createDefaultLangObject("");
    const ruValue = nextTitle.find((item) => ["ru", "Ru"].includes(item.lang));
    const enValue = nextTitle.find((item) => ["en", "En"].includes(item.lang));

    return [
        { lang: "ru", name: ruValue?.name || "" },
        { lang: "en", name: enValue?.name || "" },
    ];
};

const updateTitleByLang = (title: LangObject[] | undefined, lang: "ru" | "en", value: string): LangObject[] => {
    const nextTitle = toSafeTitle(title);
    return nextTitle.map((item) => (item.lang === lang ? { ...item, name: value } : item));
};

const normalizeItems = (items?: IExternalShowcase[]) =>
    Array.isArray(items)
        ? items.map((item: any) => ({
              // backward compatibility: support old shape with titleRu/titleEn
              title: Array.isArray(item?.title)
                  ? toSafeTitle(item.title)
                  : updateTitleByLang(updateTitleByLang(createDefaultLangObject(""), "ru", item?.titleRu || ""), "en", item?.titleEn || ""),
              context: item?.context || "",
              subtitle: Array.isArray(item?.subtitle)
                  ? toSafeTitle(item.subtitle)
                  : updateTitleByLang(updateTitleByLang(createDefaultLangObject(""), "ru", item?.subtitleRu || ""), "en", item?.subtitleEn || ""),
          }))
        : [];

const isCompleteShowcase = (item: IExternalShowcase) => {
    const titleRu = Lang(item.title || [], "ru").trim();
    const titleEn = Lang(item.title || [], "en").trim();
    const subtitleRu = Lang(item.subtitle || [], "ru").trim();
    const subtitleEn = Lang(item.subtitle || [], "en").trim();
    const context = (item.context || "").trim();

    return Boolean((titleRu || titleEn) && context);
};

const sanitizeItems = (items: IExternalShowcase[]) =>
    items
        .map((item) => ({
            title: updateTitleByLang(
                updateTitleByLang(item.title || createDefaultLangObject(""), "ru", Lang(item.title || [], "ru").trim()),
                "en",
                Lang(item.title || [], "en").trim()
            ),
            context: (item.context ?? "").trim(),
            subtitle: updateTitleByLang(
                updateTitleByLang(item.subtitle || createDefaultLangObject(""), "ru", Lang(item.subtitle || [], "ru").trim()),
                "en",
                Lang(item.subtitle || [], "en").trim()
            ),
        }))
        .filter(isCompleteShowcase);

const ExternalShowcasesEditModal: FC<ExternalShowcasesEditModalProps> = ({
    items,
    isPublicShowcase,
    onSave,
}) => {
    const { t } = useTranslation();
    const modal = useModal();
    const [draft, setDraft] = useState<IExternalShowcase[]>(normalizeItems(items));
    const [isPublicShowcaseDraft, setIsPublicShowcaseDraft] = useState(isPublicShowcase === true);

    useEffect(() => {
        setDraft(normalizeItems(items));
    }, [items]);

    useEffect(() => {
        setIsPublicShowcaseDraft(isPublicShowcase === true);
    }, [isPublicShowcase]);

    const isDraftValid = draft.every(isCompleteShowcase);

    const onContextChange = (index: number, value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (itemIndex === index ? { ...item, context: value } : item))
        );
    };

    const onTitleChange = (index: number, lang: "ru" | "en", value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (
                itemIndex === index
                    ? {
                          ...item,
                          title: updateTitleByLang(item.title || createDefaultLangObject(""), lang, value),
                      }
                    : item
            ))
        );
    };

    const onSubtitleChange = (index: number, lang: "ru" | "en", value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (itemIndex === index ? { ...item, subtitle: updateTitleByLang(item.subtitle || createDefaultLangObject(""), lang, value) } : item))
        );
    };

    const onDeleteRow = (index: number) => {
        setDraft((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    };

    const onAddRow = () => {
        setDraft((prev) => [...prev, getEmptyShowcase()]);
    };

    const onToggleIsPublicShowcase = () => {
        setIsPublicShowcaseDraft((prev) => !prev);
    };

    return (
        <Wrapper>
            <FlexBox direction="column" gy={16}>
                <h3>{t("teams.adminPage.dropdownButtons.externalShowcases.title")}</h3>
                <ScrollContainer>
                    {draft.map((item, index) => (
                        <Row key={`external_showcase_${index}`}>
                            <Column>
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseName")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.title || createDefaultLangObject("")}
                                    onChange={(ev: any, lang?: string) => onTitleChange(index, lang === "en" ? "en" : "ru", ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseName")}
                                    useLangSwitcher
                                    langSwitcherLabels={getTeamEditLangSwitcherLabels()}
                                    onWhiteBG
                                />
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseSubtitle")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.subtitle || createDefaultLangObject("")}
                                    onChange={(ev: any, lang?: string) => onSubtitleChange(index, lang === "en" ? "en" : "ru", ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseSubtitle")}
                                    useLangSwitcher
                                    langSwitcherLabels={getTeamEditLangSwitcherLabels()}
                                    onWhiteBG
                                />
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseContext")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.context}
                                    onChange={(ev: any) => onContextChange(index, ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseContext")}
                                />
                            </Column>
                            <IconButton
                                type="button"
                                aria-label={t("teams.adminPage.dropdownButtons.externalShowcases.delete")}
                                onClick={() => onDeleteRow(index)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Row>
                    ))}
                </ScrollContainer>
                {draft.length > 0 && (
                    <PublicShowcaseCheckboxWrap>
                        <CheckboxField
                            label={t("teams.adminPage.dropdownButtons.externalShowcases.publicShowcase")}
                            checked={isPublicShowcaseDraft}
                            onClick={onToggleIsPublicShowcase}
                        />
                    </PublicShowcaseCheckboxWrap>
                )}
                <FlexBox justify="space-between">
                    <FlexBox justify="flex-start">
                        <DefaultButton wh={195} whMob={180} onClick={onAddRow}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.add")}
                        </DefaultButton>
                    </FlexBox>
                    <Footer direction="row" gx={12} justify="flex-start">
                        <DefaultButton wh={131} whMob={120} onClick={() => {
                            if (!isDraftValid) {
                                return;
                            }

                            onSave({
                                items: sanitizeItems(draft),
                                isPublicShowcase: draft.length > 0 && isPublicShowcaseDraft,
                            });
                            modal.hide();
                        }} disabled={!isDraftValid}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.save")}
                        </DefaultButton>
                        <DefaultButton wh={131} whMob={120} onClick={() => modal.hide()}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.cancel")}
                        </DefaultButton>
                    </Footer>
                </FlexBox>
            </FlexBox>
        </Wrapper>
    );
};

export default ExternalShowcasesEditModal;
import { CommonInput, FlexBox, INPUT_TYPES } from "@brg/ui";
import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import DeleteIcon from "shared/icons/DeleteIcon";
import { DefaultButton } from "../styles";
import CheckboxField from "./CheckboxField";
import { IExternalShowcase } from "../types";
import { LayoutConst, useModal } from "core";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { Lang } from "@brg/api";
import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { getTeamEditLangSwitcherLabels } from "../helpers/teamEditLangSwitcher";

const PublicShowcaseCheckboxWrap = styled.div`
    & > * > *:first-child {
        min-width: unset !important;
    }
`;

const Wrapper = styled.div`
    width: 100%;
    max-width: 860px;
    background: #ffffff;
    border-radius: 6px;
    padding: 24px;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        max-width: 760px;
        padding: 20px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
        max-width: 100%;
        padding: 20px 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        padding: 16px 12px;
    }
`;

export const ScrollContainer = styled(FlexBox)`
    overflow-y: auto;
    flex-flow: column;
    max-height: 260px;
    width: 100%;

  ::-webkit-scrollbar {
    width: 6px; /* ширина scrollbar */
    height: 61px;
  }
  ::-webkit-scrollbar-track {
    background: transparent; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 3px; /* закругления плашки */
    background: #909090;
    border: 1.55px solid rgba(0, 0, 0, 0); /* имитация марджина */
    background-clip: padding-box; /* имитация марджина */
  }

`

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
    width:100%;
`;

const Column = styled.div`
    display: flex;
    flex-flow: column;
    gap: 12px;
    width: 100%;
`;

const IconButton = styled.button`
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
`;

const Footer = styled(FlexBox)`

`;

export interface ExternalShowcasesSavePayload {
    items: IExternalShowcase[];
    isPublicShowcase: boolean;
}

interface ExternalShowcasesEditModalProps {
    items?: IExternalShowcase[];
    isPublicShowcase?: boolean;
    onSave: (payload: ExternalShowcasesSavePayload) => void;
}

const getEmptyShowcase = (): IExternalShowcase => ({
    title: createDefaultLangObject(""),
    context: "",
    subtitle: createDefaultLangObject(""),
});

const toSafeTitle = (title?: LangObject[]): LangObject[] => {
    const nextTitle = Array.isArray(title) ? [...title] : createDefaultLangObject("");
    const ruValue = nextTitle.find((item) => ["ru", "Ru"].includes(item.lang));
    const enValue = nextTitle.find((item) => ["en", "En"].includes(item.lang));

    return [
        { lang: "ru", name: ruValue?.name || "" },
        { lang: "en", name: enValue?.name || "" },
    ];
};

const updateTitleByLang = (title: LangObject[] | undefined, lang: "ru" | "en", value: string): LangObject[] => {
    const nextTitle = toSafeTitle(title);
    return nextTitle.map((item) => (item.lang === lang ? { ...item, name: value } : item));
};

const normalizeItems = (items?: IExternalShowcase[]) =>
    Array.isArray(items)
        ? items.map((item: any) => ({
              // backward compatibility: support old shape with titleRu/titleEn
              title: Array.isArray(item?.title)
                  ? toSafeTitle(item.title)
                  : updateTitleByLang(updateTitleByLang(createDefaultLangObject(""), "ru", item?.titleRu || ""), "en", item?.titleEn || ""),
              context: item?.context || "",
              subtitle: Array.isArray(item?.subtitle)
                  ? toSafeTitle(item.subtitle)
                  : updateTitleByLang(updateTitleByLang(createDefaultLangObject(""), "ru", item?.subtitleRu || ""), "en", item?.subtitleEn || ""),
          }))
        : [];

const isCompleteShowcase = (item: IExternalShowcase) => {
    const titleRu = Lang(item.title || [], "ru").trim();
    const titleEn = Lang(item.title || [], "en").trim();
    const subtitleRu = Lang(item.subtitle || [], "ru").trim();
    const subtitleEn = Lang(item.subtitle || [], "en").trim();
    const context = (item.context || "").trim();

    return Boolean((titleRu || titleEn) && context);
};

const sanitizeItems = (items: IExternalShowcase[]) =>
    items
        .map((item) => ({
            title: updateTitleByLang(
                updateTitleByLang(item.title || createDefaultLangObject(""), "ru", Lang(item.title || [], "ru").trim()),
                "en",
                Lang(item.title || [], "en").trim()
            ),
            context: (item.context ?? "").trim(),
            subtitle: updateTitleByLang(
                updateTitleByLang(item.subtitle || createDefaultLangObject(""), "ru", Lang(item.subtitle || [], "ru").trim()),
                "en",
                Lang(item.subtitle || [], "en").trim()
            ),
        }))
        .filter(isCompleteShowcase);

const ExternalShowcasesEditModal: FC<ExternalShowcasesEditModalProps> = ({
    items,
    isPublicShowcase,
    onSave,
}) => {
    const { t } = useTranslation();
    const modal = useModal();
    const [draft, setDraft] = useState<IExternalShowcase[]>(normalizeItems(items));
    const [isPublicShowcaseDraft, setIsPublicShowcaseDraft] = useState(isPublicShowcase === true);

    useEffect(() => {
        setDraft(normalizeItems(items));
    }, [items]);

    useEffect(() => {
        setIsPublicShowcaseDraft(isPublicShowcase === true);
    }, [isPublicShowcase]);

    const isDraftValid = draft.every(isCompleteShowcase);

    const onContextChange = (index: number, value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (itemIndex === index ? { ...item, context: value } : item))
        );
    };

    const onTitleChange = (index: number, lang: "ru" | "en", value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (
                itemIndex === index
                    ? {
                          ...item,
                          title: updateTitleByLang(item.title || createDefaultLangObject(""), lang, value),
                      }
                    : item
            ))
        );
    };

    const onSubtitleChange = (index: number, lang: "ru" | "en", value: string) => {
        setDraft((prev) =>
            prev.map((item, itemIndex) => (itemIndex === index ? { ...item, subtitle: updateTitleByLang(item.subtitle || createDefaultLangObject(""), lang, value) } : item))
        );
    };

    const onDeleteRow = (index: number) => {
        setDraft((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    };

    const onAddRow = () => {
        setDraft((prev) => [...prev, getEmptyShowcase()]);
    };

    const onToggleIsPublicShowcase = () => {
        setIsPublicShowcaseDraft((prev) => !prev);
    };

    return (
        <Wrapper>
            <FlexBox direction="column" gy={16}>
                <h3>{t("teams.adminPage.dropdownButtons.externalShowcases.title")}</h3>
                <ScrollContainer>
                    {draft.map((item, index) => (
                        <Row key={`external_showcase_${index}`}>
                            <Column>
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseName")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.title || createDefaultLangObject("")}
                                    onChange={(ev: any, lang?: string) => onTitleChange(index, lang === "en" ? "en" : "ru", ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseName")}
                                    useLangSwitcher
                                    langSwitcherLabels={getTeamEditLangSwitcherLabels()}
                                    onWhiteBG
                                />
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseSubtitle")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.subtitle || createDefaultLangObject("")}
                                    onChange={(ev: any, lang?: string) => onSubtitleChange(index, lang === "en" ? "en" : "ru", ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseSubtitle")}
                                    useLangSwitcher
                                    langSwitcherLabels={getTeamEditLangSwitcherLabels()}
                                    onWhiteBG
                                />
                                <CommonInput
                                    label={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseContext")}
                                    type={INPUT_TYPES.text}
                                    orientation="column"
                                    value={item.context}
                                    onChange={(ev: any) => onContextChange(index, ev.target.value)}
                                    placeholder={t("teams.adminPage.dropdownButtons.externalShowcases.showcaseContext")}
                                />
                            </Column>
                            <IconButton
                                type="button"
                                aria-label={t("teams.adminPage.dropdownButtons.externalShowcases.delete")}
                                onClick={() => onDeleteRow(index)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Row>
                    ))}
                </ScrollContainer>
                {draft.length > 0 && (
                    <PublicShowcaseCheckboxWrap>
                        <CheckboxField
                            label={t("teams.adminPage.dropdownButtons.externalShowcases.publicShowcase")}
                            checked={isPublicShowcaseDraft}
                            onClick={onToggleIsPublicShowcase}
                        />
                    </PublicShowcaseCheckboxWrap>
                )}
                <FlexBox justify="space-between">
                    <FlexBox justify="flex-start">
                        <DefaultButton wh={195} whMob={180} onClick={onAddRow}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.add")}
                        </DefaultButton>
                    </FlexBox>
                    <Footer direction="row" gx={12} justify="flex-start">
                        <DefaultButton wh={131} whMob={120} onClick={() => {
                            if (!isDraftValid) {
                                return;
                            }

                            onSave({
                                items: sanitizeItems(draft),
                                isPublicShowcase: draft.length > 0 && isPublicShowcaseDraft,
                            });
                            modal.hide();
                        }} disabled={!isDraftValid}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.save")}
                        </DefaultButton>
                        <DefaultButton wh={131} whMob={120} onClick={() => modal.hide()}>
                            {t("teams.adminPage.dropdownButtons.externalShowcases.cancel")}
                        </DefaultButton>
                    </Footer>
                </FlexBox>
            </FlexBox>
        </Wrapper>
    );
};

export default ExternalShowcasesEditModal;
